import React from "react";
import { ArrowDown, Download, Linkedin, Github } from "lucide-react";
import { personalInfo, RESUME_URL } from "../data/mock";
import axios from "axios";

const BACKEND_URL = (typeof window !== "undefined" && window.location?.origin) || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const trackResumeDownload = () => {
  axios.post(`${API}/resume/track`).catch(() => {});
};

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Subtle background elements */}
      <div className="absolute inset-0">
        {/* Gradient orb - top right */}
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-blue-500/[0.03] rounded-full blur-[120px]" />
        {/* Gradient orb - bottom left */}
        <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-blue-400/[0.02] rounded-full blur-[100px]" />
        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 py-32">
        <div className="max-w-3xl">
          {/* Status badge */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-500/8 border border-blue-500/15 mb-8 animate-fadeIn">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span className="text-xs font-medium text-zinc-400">
              Open to new opportunities
            </span>
          </div>

          {/* Name */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white tracking-tight leading-[1.05] mb-4 animate-fadeInUp">
            {personalInfo.name}
          </h1>

          {/* Title */}
          <p className="text-xl sm:text-2xl font-medium text-blue-400 mb-4 animate-fadeInUp animation-delay-100">
            {personalInfo.title}
            <span className="text-zinc-600 mx-2">|</span>
            <span className="text-zinc-400 font-normal">
              {personalInfo.subtitle}
            </span>
          </p>

          {/* Value Proposition */}
          <p className="text-base sm:text-lg text-zinc-500 max-w-xl leading-relaxed mb-10 animate-fadeInUp animation-delay-200">
            {personalInfo.valueProposition}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap items-center gap-4 mb-12 animate-fadeInUp animation-delay-300">
            <button
              onClick={() => {
                const el = document.getElementById("projects");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="group inline-flex items-center gap-2.5 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/20"
            >
              View Projects
              <ArrowDown size={16} className="transition-transform duration-200 group-hover:translate-y-0.5" />
            </button>

            <a
              href={RESUME_URL}
              target="_blank"
              rel="noopener noreferrer"
              download
              onClick={trackResumeDownload}
              className="group inline-flex items-center gap-2.5 px-6 py-3 bg-white/5 hover:bg-white/10 text-white text-sm font-medium rounded-lg border border-white/10 hover:border-white/20 transition-all duration-200"
            >
              Download Resume
              <Download size={16} className="transition-transform duration-200 group-hover:translate-y-0.5" />
            </a>
          </div>

          {/* Social Links */}
          <div className="flex items-center gap-4 animate-fadeInUp animation-delay-400">
            <a
              href={personalInfo.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-zinc-500 hover:text-blue-400 transition-colors duration-200 text-sm"
            >
              <Linkedin size={18} />
              <span>LinkedIn</span>
            </a>
            <span className="text-zinc-700">•</span>
            <a
              href={personalInfo.github}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors duration-200 text-sm"
            >
              <Github size={18} />
              <span>GitHub</span>
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-fadeIn animation-delay-500">
        <div className="flex flex-col items-center gap-2">
          <span className="text-[11px] uppercase tracking-widest text-zinc-600 font-medium">
            Scroll
          </span>
          <div className="w-[1px] h-8 bg-gradient-to-b from-zinc-600 to-transparent animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
